import { FunctionComponent, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { AxiosResponse } from "axios";
import { ReactComponent as ExcelIcon } from "../../features/FloorManager/assets/xlsx-file-format-extension.svg";
import { FileObject } from "../../features/FileDropzone/typings/FileObject.type";
import Dropzone from "../../features/FileDropzone/Dropzone.component";

interface OwnProps {
  uploadCall: (excel: FileObject) => Promise<AxiosResponse<any>> | undefined;
  initialFiles?: FileObject[];
  triggerCall?: () => void;
  onFinishUpload?: () => void;
}

type Props = OwnProps;

const ExcelUploadModal: FunctionComponent<Props> = ({
  uploadCall,
  initialFiles,
  triggerCall,
  onFinishUpload
}) => {
  const [files, setFiles] = useState<FileObject[]>(initialFiles || []); // vx
  const [excelOpen, setExcelOpen] = useState(false);
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  async function handleClickUpload() {
    setExcelOpen(false);
    if (!files?.length || files[0] === undefined) return;

    // @ts-ignore
    try {
      await uploadCall(files[0]);
      onFinishUpload?.();
      setFiles([]);
      if (triggerCall) triggerCall();
    } catch (e: unknown) {
      const error = e as any;
      const message =
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : undefined;
      if (message?.includes(`Can't find end of central directory`)) {
        enqueueSnackbar(
          t(`The file is not a valid Excel file. Please check the file and try again`),
          { variant: "error" }
        );
      } else {
        enqueueSnackbar(t(`${message ?? "Unknown error"}`), { variant: "error" });
      }
    }
  }

  function handleDialogClose() {
    setExcelOpen(false);
    setFiles([]);
  }

  const acceptableFiles: Record<string, string[]> = {
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
    "text/csv": [".csv"]
  };

  return (
    <>
      <Tooltip title={<Typography>{t("Import")}</Typography>} aria-label={"Import"}>
        <IconButton
          onClick={() => setExcelOpen(true)}
          aria-label="delete"
          color="primary"
          size="large"
        >
          <ExcelIcon style={{ width: 20, height: 20, fill: theme.palette.text.primary }} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={excelOpen}
        keepMounted
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="alert-dialog-slide-title">{t("Import data from Excel")}</DialogTitle>
        <DialogContent>
          <Dropzone
            data-testid={"inputDrop"}
            files={files}
            dropzoneDesc={t("Drop excel or click to upload") + " " + "(.xlsx, .xls)"}
            acceptedFileTypes={acceptableFiles}
            maxFiles={1}
            onAdd={f => setFiles(f)}
            onDelete={(_file, index) => setFiles(prev => prev.filter((_, i) => i !== index))}
            thumbWidth="auto"
          />
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="excel-upload-btn-close"
            onClick={handleDialogClose}
            color={"secondary"}
          >
            {t("Close")}
          </Button>
          {files && files.length > 0 && (
            <Button
              data-testid="excel-upload-btn-upload"
              onClick={handleClickUpload}
              color="primary"
            >
              {t("Upload")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExcelUploadModal;
